/**
 * J2 — AOI Calculator (admin-only).
 *
 * 4 sub-tabs:
 *   • Calculator     — state + district pickers, 7 parameter sections each
 *                      with their sub-component inputs (sliders + number boxes),
 *                      live AOI score + centre-potential band, Save button.
 *   • Formula        — the exact weighted formula per category, lifted from the
 *                      workbook's Formula sheet.
 *   • Scoring Logic  — the score-band → meaning lookup tables.
 *   • Saved History  — every previously-saved scorecard, filterable by state /
 *                      district. Click any row to re-load it into the
 *                      Calculator for editing or comparison.
 *
 * Live preview: as soon as admin tweaks any input, we POST /api/aoi/compute
 * (debounced 200 ms) and update the score header. Saving creates a new
 * history record — re-scoring a district does NOT overwrite the previous one,
 * so admins can track movement over time.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    Calculator, MapPin, Save, RefreshCw, Trash2, History, FileText,
    ListChecks, Sparkles, ChevronRight, BookOpen, AlertTriangle,
    Cloud, Zap, Info, Download, Globe2, Layers,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import {
    AOI_CATEGORIES as CATEGORIES,
    emptyAoiInputs as emptyInputs,
    aoiScoreTone as scoreTone,
    countEmptyAoiMerge,
} from '../../constants/aoiCategories';

/** District dropdown value for state-wide auto-fetch / scoring. */
export const ALL_DISTRICTS = '__ALL__';

export function districtDisplayName(district, state) {
    if (district === ALL_DISTRICTS || district === 'All districts (state total)') {
        return `All districts — ${state}`;
    }
    return district;
}

export function districtSaveName(district) {
    return district === ALL_DISTRICTS ? 'All districts (state total)' : district;
}

export function districtFromSaved(saved) {
    return saved === 'All districts (state total)' ? ALL_DISTRICTS : saved;
}

const SUB_TABS = [
    { id: 'calc', label: 'Calculator', icon: Calculator },
    { id: 'formula', label: 'Formula', icon: FileText },
    { id: 'scoring', label: 'Scoring Logic', icon: BookOpen },
    { id: 'history', label: 'Saved History', icon: History },
];

export default function AoiCalculator() {
    const [subTab, setSubTab] = useState('calc');
    const [territories, setTerritories] = useState([]);
    const [meta, setMeta] = useState(null);
    const [state, setState] = useState('');
    const [district, setDistrict] = useState('');
    const [inputs, setInputs] = useState(emptyInputs());
    const [notes, setNotes] = useState('');
    const [preview, setPreview] = useState({ aoiScore: 0, categoryScores: {}, centrePotential: '', territoryType: '' });
    const [history, setHistory] = useState([]);
    const [savingBusy, setSavingBusy] = useState(false);
    const [historyFilter, setHistoryFilter] = useState({ state: '', district: '' });
    const debounceRef = useRef(null);
    const [fetched, setFetched] = useState(null);
    const [fetching, setFetching] = useState(false);
    const [downloadingLive, setDownloadingLive] = useState(false);
    const [stateCentreRollup, setStateCentreRollup] = useState(null);
    const [batchProgress, setBatchProgress] = useState(null);
    const [stateBatchDone, setStateBatchDone] = useState(false);

    /* Clear stale auto-fetch when territory changes. */
    useEffect(() => {
        setFetched(null);
        setStateCentreRollup(null);
        setBatchProgress(null);
        setStateBatchDone(false);
    }, [state, district]);

    /* State-wide centre rollup — sum per-district bands, not one aggregate label. */
    useEffect(() => {
        if (!state || district !== ALL_DISTRICTS) {
            setStateCentreRollup(null);
            return;
        }
        let cancelled = false;
        api.get('/aoi/state-centres', { params: { state } })
            .then(r => { if (!cancelled) setStateCentreRollup(r.data); })
            .catch(() => { if (!cancelled) setStateCentreRollup(null); });
        return () => { cancelled = true; };
    }, [state, district]);

    /* Load territories + metadata once. */
    useEffect(() => {
        api.get('/aoi/territories').then(r => setTerritories(r.data.items || []));
        api.get('/aoi/metadata').then(r => setMeta(r.data));
    }, []);

    /* Load history when its tab opens or filters change. */
    useEffect(() => {
        if (subTab !== 'history') return;
        const params = {};
        if (historyFilter.state) params.state = historyFilter.state;
        if (historyFilter.district) params.district = historyFilter.district;
        api.get('/aoi/scores', { params }).then(r => setHistory(r.data.items || []));
    }, [subTab, historyFilter.state, historyFilter.district]);

    /* Debounced live preview — fire 200 ms after the last input change. */
    useEffect(() => {
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            api.post('/aoi/compute', inputs).then(r => setPreview(r.data));
        }, 200);
        return () => clearTimeout(debounceRef.current);
    }, [inputs]);

    const stateOptions = territories;
    const districtOptions = useMemo(
        () => territories.find(t => t.state === state)?.districts || [],
        [territories, state],
    );

    const setSub = (cat, sub, v) => {
        const num = v === '' ? 0 : Math.max(0, Math.min(100, Number(v)));
        setInputs(prev => ({ ...prev, [cat]: { ...prev[cat], [sub]: Number.isNaN(num) ? 0 : num } }));
    };

    const reset = () => {
        setInputs(emptyInputs());
        setNotes('');
        setFetched(null);
    };

    const applySuggestion = (catKey, subKey, value) => {
        setInputs(prev => {
            const current = prev[catKey]?.[subKey] ?? 0;
            if (current !== 0) return prev;
            return {
                ...prev,
                [catKey]: { ...prev[catKey], [subKey]: value },
            };
        });
    };

    const countEmptyMerge = (prev, suggestions) => countEmptyAoiMerge(prev, suggestions);

    const save = async () => {
        if (!state || !district) {
            return toast.error('Pick a state and district before saving');
        }
        setSavingBusy(true);
        try {
            await api.post('/aoi/scores', {
                state,
                district: districtSaveName(district),
                notes,
                ...inputs,
            });
            toast.success(`Saved · ${districtDisplayName(district, state)} → AOI ${preview.aoiScore} (${preview.centrePotential})`);
            /* Refresh history if it's mounted in the background. */
            if (subTab === 'history') {
                const params = {};
                if (historyFilter.state) params.state = historyFilter.state;
                if (historyFilter.district) params.district = historyFilter.district;
                const r = await api.get('/aoi/scores', { params });
                setHistory(r.data.items || []);
            }
        } catch (e) {
            toast.error(e?.response?.data?.error || 'Save failed');
        } finally { setSavingBusy(false); }
    };

    const downloadStateExcel = async () => {
        setDownloadingLive(true);
        try {
            const res = await api.post('/aoi/export/live', {
                state,
                district: districtSaveName(ALL_DISTRICTS),
            }, { responseType: 'blob', timeout: 120_000 });
            const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            const safe = (s) => String(s || '').replace(/[^a-z0-9]+/gi, '-');
            a.download = `aoi-${safe(state)}-districts-${new Date().toISOString().slice(0, 10)}.xlsx`;
            a.click();
            toast.success('Downloaded Excel with fetched district data');
        } catch (e) {
            toast.error(e?.response?.data?.error || 'Download failed');
        } finally {
            setDownloadingLive(false);
        }
    };

    /** All-districts mode: server-side batch with Overpass rate limiting + retries. */
    const fetchAllDistrictsBatch = async ({ resetFirst = false, downloadAfter = true } = {}) => {
        if (!state || district !== ALL_DISTRICTS) return;
        if (!districtOptions.length) return toast.error('No districts found for this state');
        setFetching(true);
        setStateBatchDone(false);
        let pollTimer = null;
        try {
            pollTimer = setInterval(async () => {
                try {
                    const { data } = await api.get('/aoi/autofetch/batch/status', { params: { state } });
                    if (data?.running) {
                        setBatchProgress({
                            current: data.current,
                            total: data.total,
                            name: data.name,
                        });
                    }
                } catch { /* ignore poll errors */ }
            }, 2000);

            const { data } = await api.post('/aoi/autofetch/batch', { state, force: resetFirst }, {
                timeout: 45 * 60_000,
            });

            if (data.stateCentreRollup) setStateCentreRollup(data.stateCentreRollup);
            setFetched({
                batch: true,
                scope: 'state',
                state,
                stateDistrictCount: data.total,
                ok: data.ok,
                failed: data.failed,
                tookMs: data.tookMs,
            });
            setStateBatchDone(true);
            if (data.failed === 0) {
                toast.success(`Fetched all ${data.ok} districts — building Excel…`);
            } else {
                const failedNames = (data.results || []).filter(r => !r.ok).map(r => r.district);
                toast(`${data.ok} districts fetched, ${data.failed} failed (${failedNames.slice(0, 3).join(', ')}${failedNames.length > 3 ? '…' : ''})`, { icon: '⚠️' });
            }
            if (downloadAfter) await downloadStateExcel();
        } catch (e) {
            const timedOut = e.code === 'ECONNABORTED' || /timeout/i.test(e.message || '');
            toast.error(
                timedOut
                    ? 'Batch fetch timed out — try again or fetch fewer districts at a time'
                    : (e?.response?.data?.error || e.message || 'Batch fetch failed'),
                { duration: 8000 },
            );
        } finally {
            if (pollTimer) clearInterval(pollTimer);
            setBatchProgress(null);
            setFetching(false);
        }
    };

    /* J4 — auto-fetch from external sources. Fills empty fields only;
       admin's typed values are never overwritten. */
    const autoFetch = async ({ resetFirst = false } = {}) => {
        if (!state || !district) {
            return toast.error('Pick state & district');
        }
        if (district === ALL_DISTRICTS && state) {
            return fetchAllDistrictsBatch({ resetFirst, downloadAfter: true });
        }
        setFetching(true);
        try {
            const { data } = await api.post('/aoi/autofetch', {
                state: state || undefined,
                district: district || undefined,
                force: !!resetFirst,
            }, {
                /* The server now enforces its own ~40s budget and always
                   responds, so this only needs headroom above that. */
                timeout: 90_000,
            });
            if (data.resolvedTerritory?.state) setState(data.resolvedTerritory.state);
            if (data.resolvedTerritory?.district) {
                setDistrict(districtFromSaved(data.resolvedTerritory.district));
            }
            setFetched(data);
            if (data.stateCentreRollup) setStateCentreRollup(data.stateCentreRollup);
            let merged = 0;
            setInputs(prev => {
                const base = resetFirst ? emptyInputs() : prev;
                merged = countEmptyMerge(base, data.suggestions);
                const next = { ...base };
                for (const [cat, subs] of Object.entries(data.suggestions || {})) {
                    next[cat] = { ...base[cat] };
                    for (const [k, v] of Object.entries(subs || {})) {
                        if (resetFirst || !base[cat]?.[k]) {
                            next[cat][k] = v;
                        }
                    }
                }
                return next;
            });
            const sources = [
                data.providers?.osm?.counts ? 'OSM' : null,
                data.providers?.wiki?.stats ? 'Wikidata' : null,
                data.providers?.google?.counts ? 'Google Places' : null,
            ].filter(Boolean).join(' + ') || 'partial data';
            if (merged === 0 && !resetFirst) {
                toast('All fields already have values — use Reset & fetch to replace them', { icon: 'ℹ️' });
            } else {
                toast.success(`Fetched ${merged} field${merged === 1 ? '' : 's'} from ${sources} (${(data.tookMs / 1000).toFixed(1)}s)`);
            }
            if (data.degraded) {
                const down = Object.entries(data.providerStatus || {})
                    .filter(([, v]) => v !== 'ok')
                    .map(([k, v]) => `${k}: ${v}`)
                    .join(', ');
                toast(`Partial data — ${down}. Some values fall back to estimates.`, {
                    icon: '⚠️', duration: 6000,
                });
            }
        } catch (e) {
            const timedOut = e.code === 'ECONNABORTED' || /timeout/i.test(e.message || '');
            toast.error(
                timedOut ? 'Auto-fetch timed out — try again' : (e?.response?.data?.error || e.message || 'Fetch failed — check backend logs'),
                { duration: 6000 },
            );
        } finally { setFetching(false); }
    };

    const downloadLive = async () => {
        if (!state || !district) return;
        if (district === ALL_DISTRICTS) {
            if (!stateBatchDone && !fetched?.batch) {
                return toast.error('Fetch all districts first — each district is loaded one by one, then Excel is built');
            }
            return downloadStateExcel();
        }
        if (!fetched) return;
        setDownloadingLive(true);
        try {
            const res = await api.post('/aoi/export/live', {
                state,
                district: districtSaveName(district),
                inputs,
            }, { responseType: 'blob', timeout: 120_000 });
            const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            const safe = (s) => String(s || '').replace(/[^a-z0-9]+/gi, '-');
            a.download = `aoi-${safe(district)}-${safe(state)}-${new Date().toISOString().slice(0, 10)}.xlsx`;
            a.click();
            toast.success('Downloaded Excel with fetched results');
        } catch (e) {
            toast.error(e?.response?.data?.error || 'Download failed');
        } finally {
            setDownloadingLive(false);
        }
    };

    const loadFromHistory = (item) => {
        setState(item.state);
        setDistrict(districtFromSaved(item.district));
        setNotes(item.notes || '');
        setFetched(null);
        const next = emptyInputs();
        for (const c of CATEGORIES) {
            for (const s of c.subs) {
                next[c.key][s.key] = item[c.key]?.[s.key] ?? 0;
            }
        }
        setInputs(next);
        setSubTab('calc');
        toast.success(`Loaded ${item.district}, ${item.state}`);
    };

    const removeHistory = async (id, label) => {
        if (!confirm(`Delete this score for ${label}?`)) return;
        try {
            await api.delete(`/aoi/scores/${id}`);
            setHistory(h => h.filter(x => x._id !== id));
            toast.success('Deleted');
        } catch (e) { toast.error(e?.response?.data?.error || 'Delete failed'); }
    };

    return (
        <div className="space-y-3">
            {/* Page header */}
            <div className="flex items-start justify-between flex-wrap gap-2">
                <div>
                    <h1 className="text-2xl font-extrabold flex items-center gap-2">
                        <Sparkles className="h-5 w-5 text-brand-teal-700" /> AOI Calculator
                    </h1>
                    <p className="text-sm text-ink-500 mt-1 max-w-3xl">
                        Score any district on the 7-parameter weighted model from the AGEON trial sheet. Pick a
                        state and district, enter each sub-component on a 0-100 scale, and the AOI score updates
                        live. Each save creates a fresh history entry so you can track how a market evolves.
                    </p>
                </div>
            </div>

            {/* Sub-tab strip + live score (calculator tab) */}
            <div className="ag-card p-2 flex flex-wrap items-center gap-2">
                <div className="flex flex-wrap gap-1">
                    {SUB_TABS.map(t => (
                        <button key={t.id} onClick={() => setSubTab(t.id)}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition
                                ${subTab === t.id ? 'bg-brand-teal-600 text-[#02b09b]' : 'text-ink-700 hover:bg-ink-100'}`}>
                            <t.icon className="h-4 w-4" /> {t.label}
                        </button>
                    ))}
                </div>
                {subTab === 'calc' && (
                    <div className="w-full sm:w-auto sm:ml-auto">
                        <ScoreHeadline
                            preview={preview}
                            stateWide={district === ALL_DISTRICTS}
                            state={state}
                            stateCentreRollup={stateCentreRollup}
                            inline
                        />
                    </div>
                )}
            </div>

            {subTab === 'calc' && (
                <CalculatorTab
                    stateOptions={stateOptions}
                    districtOptions={districtOptions}
                    state={state}
                    setState={s => { setState(s); setDistrict(''); }}
                    district={district}
                    setDistrict={setDistrict}
                    inputs={inputs} setSub={setSub}
                    preview={preview}
                    stateCentreRollup={stateCentreRollup}
                    stateWide={district === ALL_DISTRICTS}
                    notes={notes} setNotes={setNotes}
                    onSave={save} onReset={reset} saving={savingBusy}
                    fetched={fetched} fetching={fetching} onAutoFetch={autoFetch}
                    onApplySuggestion={applySuggestion}
                    onDownloadLive={downloadLive}
                    downloadingLive={downloadingLive}
                    batchProgress={batchProgress}
                    stateBatchDone={stateBatchDone}
                    onFetchAllDistricts={fetchAllDistrictsBatch}
                />
            )}

            {subTab === 'formula' && <FormulaTab meta={meta} />}
            {subTab === 'scoring' && <ScoringTab meta={meta} />}
            {subTab === 'history' && (
                <HistoryTab
                    items={history}
                    filter={historyFilter}
                    setFilter={setHistoryFilter}
                    onLoad={loadFromHistory}
                    onDelete={removeHistory}
                    stateOptions={stateOptions}
                />
            )}
        </div>
    );
}

/* ─── Calculator tab ─────────────────────────────────────────────────────── */
function CalculatorTab({
    stateOptions, districtOptions,
    state, setState, district, setDistrict,
    inputs, setSub,
    preview, stateCentreRollup, stateWide,
    notes, setNotes,
    onSave, onReset, saving,
    fetched, fetching, onAutoFetch, onApplySuggestion,
    onDownloadLive, downloadingLive,
    batchProgress, stateBatchDone, onFetchAllDistricts,
}) {
    const canFetch = Boolean(state && district);
    const batchBusy = Boolean(batchProgress);

    return (
        <div className="space-y-3">
            <div className="ag-card overflow-hidden">
                <div className="p-3 flex flex-col gap-3 lg:flex-row lg:items-end lg:flex-wrap">
                    <div className="flex flex-col sm:flex-row gap-3 shrink-0">
                        <div className="w-full sm:w-56">
                            <label className="ag-label inline-flex items-center gap-1">
                                <MapPin className="h-3.5 w-3.5" /> State / UT
                            </label>
                            <select
                                className="ag-input w-full"
                                value={state}
                                onChange={e => {
                                    setState(e.target.value);
                                    setDistrict('');
                                }}
                            >
                                <option value="">— pick a state —</option>
                                {stateOptions.map(s => (
                                    <option key={s.state} value={s.state}>
                                        {s.state}{s.type === 'ut' ? ' (UT)' : ''} · {s.count} districts
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="w-full sm:w-56">
                            <label className="ag-label">District</label>
                            <select
                                className="ag-input w-full"
                                value={district}
                                onChange={e => setDistrict(e.target.value)}
                                disabled={!state}
                            >
                                <option value="">{state ? '— pick a district —' : 'pick a state first'}</option>
                                {state && (
                                    <option value={ALL_DISTRICTS}>
                                        All districts — state total ({districtOptions.length} districts)
                                    </option>
                                )}
                                {district && !districtOptions.includes(district) && district !== ALL_DISTRICTS && (
                                    <option value={district}>{district}</option>
                                )}
                                {districtOptions.map(d => <option key={d} value={d}>{d}</option>)}
                            </select>
                        </div>
                    </div>
                    <FetchActionButtons
                        stateWide={stateWide}
                        canFetch={canFetch}
                        fetching={fetching}
                        fetched={fetched}
                        downloadingLive={downloadingLive}
                        batchBusy={batchBusy}
                        batchProgress={batchProgress}
                        stateBatchDone={stateBatchDone}
                        onDownloadLive={onDownloadLive}
                        onAutoFetch={onAutoFetch}
                        onFetchAllDistricts={onFetchAllDistricts}
                    />
                </div>

                <div className="border-t border-ink-100 px-3 py-2.5 bg-gradient-to-r from-brand-teal-50/50 to-white">
                    <div className="font-semibold text-sm flex items-center gap-2">
                        <Cloud className="h-4 w-4 text-brand-teal-700 shrink-0" />
                        Fetch market data
                    </div>
                    <p className="text-xs text-ink-600 mt-1">
                        {stateWide ? (
                            <>Fetch each district from OSM / Wikidata / Google, then download one Excel.</>
                        ) : (
                            <>Pull POI counts from OSM, Wikidata, and Google. Fills empty fields only — use Reset &amp; fetch to replace all.</>
                        )}
                    </p>
                    {batchProgress && (
                        <p className="text-[11px] text-brand-teal-800 mt-2 font-semibold">
                            Fetching {batchProgress.current}/{batchProgress.total}: {batchProgress.name}…
                            <span className="block mt-1 h-1.5 rounded-full bg-brand-teal-100 overflow-hidden max-w-md">
                                <span
                                    className="block h-full bg-brand-teal-600 transition-all"
                                    style={{ width: `${Math.round((batchProgress.current / batchProgress.total) * 100)}%` }}
                                />
                            </span>
                        </p>
                    )}
                    {fetched?.tookMs != null && (
                        <p className="text-[10px] text-emerald-700 mt-1 font-medium">
                            Last fetch: {(fetched.tookMs / 1000).toFixed(1)}s
                            {fetched.providers?.osm?.counts ? ' · OSM OK' : fetched.providers?.osm?.error ? ' · OSM failed' : ''}
                            {fetched.providers?.wiki?.stats ? ' · Wikidata OK' : ''}
                            {fetched.providers?.google?.counts ? ' · Google OK' : fetched.providers?.google?.skipped ? ' · Google skipped (no API key)' : ''}
                        </p>
                    )}
                </div>
            </div>

            {fetched?.batch && (
                <div className="ag-card p-4 border-emerald-200/60 bg-emerald-50/40 text-sm">
                    <span className="font-semibold text-emerald-900">
                        {fetched.state} — per-district fetch complete
                    </span>
                    <p className="text-xs text-emerald-800 mt-1">
                        {fetched.ok} of {fetched.stateDistrictCount} districts fetched from live sources
                        {fetched.failed ? ` · ${fetched.failed} failed` : ''}. Excel uses this data row-by-row.
                    </p>
                </div>
            )}

            {fetched && !fetched.batch && (
                <AutoFetchSummary fetched={fetched} district={district} state={state} />
            )}

            {/* J6 — Per-district centre breakdown when admin picks "All districts" */}
            {stateWide && (
                <StateBreakdownPanel
                    state={state}
                    fetching={fetching}
                    batchProgress={batchProgress}
                    refreshKey={stateBatchDone ? 'done' : state}
                    onFetchAllAndDownload={() => onFetchAllDistricts({ resetFirst: false, downloadAfter: true })}
                />
            )}

            {/* Parameter sections */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {CATEGORIES.map(cat => (
                    <CategoryCard
                        key={cat.key}
                        cat={cat}
                        values={inputs[cat.key]}
                        suggestions={fetched?.suggestions?.[cat.key]}
                        confidence={fetched?.confidence?.[cat.key]}
                        categoryScore={preview.categoryScores?.[cat.key] ?? 0}
                        onChange={(sub, v) => setSub(cat.key, sub, v)}
                        onApplySuggestion={(sub, v) => onApplySuggestion(cat.key, sub, v)}
                    />
                ))}
            </div>

            {/* Notes + Save */}
            <div className="ag-card p-4 space-y-3">
                <div>
                    <label className="ag-label">Notes (optional)</label>
                    <textarea className="ag-input min-h-[60px]" value={notes} onChange={e => setNotes(e.target.value)}
                        placeholder="What's driving this score? Mention any unusual data sources or assumptions." />
                </div>
                <div className="flex flex-wrap justify-end gap-2">
                    <button type="button" className="ag-btn-ghost" onClick={onReset}>
                        <RefreshCw className="h-4 w-4" /> Reset inputs
                    </button>
                    <button className="ag-btn-primary" onClick={onSave} disabled={saving || !state || !district}>
                        <Save className="h-4 w-4" /> {saving ? 'Saving…' : 'Save scorecard'}
                    </button>
                </div>
            </div>
        </div>
    );
}

/* J6 — Per-district centre breakdown for a state. Hits /aoi/state-summary
   which walks every district, uses the latest saved score where available,
   otherwise classifies by tier. Big headline = state total (min–max centres),
   table = each district with source chip + AOI estimate + centre band. */
function StateBreakdownPanel({ state, fetching, batchProgress, refreshKey, onFetchAllAndDownload }) {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');

    useEffect(() => {
        if (!state) return;
        let cancelled = false;
        setBusy(true); setErr('');
        api.get('/aoi/state-summary', { params: { state } })
            .then(r => { if (!cancelled) setData(r.data); })
            .catch(e => { if (!cancelled) setErr(e?.response?.data?.error || 'Could not load state breakdown'); })
            .finally(() => { if (!cancelled) setBusy(false); });
        return () => { cancelled = true; };
    }, [state, refreshKey]);

    if (busy && !data) {
        return <div className="ag-card p-6 text-sm text-ink-500">Loading {state} breakdown…</div>;
    }
    if (err) {
        return <div className="ag-card p-4 text-sm text-rose-700 bg-rose-50">{err}</div>;
    }
    if (!data) return null;

    const t = data.totals;
    return (
        <div className="ag-card overflow-hidden border border-emerald-200">
            <div className="px-4 py-3 bg-emerald-50 border-b border-emerald-200 flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                    <div className="font-extrabold text-sm flex items-center gap-2 text-emerald-900">
                        <Layers className="h-4 w-4" /> {data.state} · per-district centre breakdown
                    </div>
                    <div className="text-[11px] text-emerald-800 mt-1">
                        {t.districtCount} districts · {t.scoredCount} with saved scorecards · {t.districtCount - t.scoredCount} tier-default estimates.
                        Tier defaults: tier-1 ≈ 8-12, tier-2 ≈ 3-5, tier-3 ≈ 1-3.
                    </div>
                </div>
                <div className="rounded-lg bg-white border border-emerald-200 px-3 py-2 min-w-[200px]">
                    <div className="text-[10px] uppercase tracking-wide font-bold text-emerald-700">State total centres</div>
                    <div className="text-2xl font-extrabold tabular-nums text-emerald-900 mt-0.5">
                        {t.minCentres}–{t.maxCentres}
                    </div>
                    <div className="text-[10px] text-emerald-700 mt-0.5">avg district AOI {t.avgAoi}</div>
                    <button
                        type="button"
                        className="ag-btn-primary text-[11px] mt-2 w-full justify-center"
                        disabled={fetching}
                        onClick={onFetchAllAndDownload}
                    >
                        <Download className="h-3.5 w-3.5" />
                        {batchProgress
                            ? `${batchProgress.current}/${batchProgress.total}…`
                            : fetching ? 'Fetching…' : 'Fetch all & download Excel'}
                    </button>
                </div>
            </div>
            <div className="overflow-x-auto max-h-[420px]">
                <table className="w-full text-xs">
                    <thead className="bg-ink-50 sticky top-0 z-10">
                        <tr className="text-[10px] uppercase tracking-wide text-ink-500">
                            <th className="text-left px-3 py-2">District</th>
                            <th className="text-left px-3 py-2">Tier</th>
                            <th className="text-left px-3 py-2">Source</th>
                            <th className="text-right px-3 py-2">AOI</th>
                            <th className="text-left px-3 py-2">Centre potential</th>
                            <th className="text-right px-3 py-2">Min–max</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-ink-100">
                        {data.districts.map(d => (
                            <tr key={d.district} className="hover:bg-emerald-50/40">
                                <td className="px-3 py-1.5 font-medium">{d.district}</td>
                                <td className="px-3 py-1.5">
                                    <span className={`ag-chip text-[10px] ${
                                        d.tier === 'tier1' ? 'bg-emerald-100 text-emerald-800' :
                                        d.tier === 'tier2' ? 'bg-brand-teal-50 text-brand-teal-800' :
                                        'bg-ink-100 text-ink-700'}`}>
                                        {d.tier}
                                    </span>
                                </td>
                                <td className="px-3 py-1.5">
                                    <span className={`text-[10px] ${d.source === 'saved' ? 'text-emerald-700 font-semibold' : 'text-ink-500'}`}>
                                        {d.source === 'saved' ? '✓ saved scorecard' : 'tier default'}
                                    </span>
                                </td>
                                <td className="px-3 py-1.5 text-right tabular-nums font-mono">{Number(d.aoiScore).toFixed(1)}</td>
                                <td className="px-3 py-1.5 text-xs">{d.centrePotential}</td>
                                <td className="px-3 py-1.5 text-right tabular-nums">{d.minCentres}–{d.maxCentres}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function FetchActionButtons({
    stateWide, canFetch, fetching, fetched, downloadingLive,
    batchBusy, batchProgress, stateBatchDone,
    onDownloadLive, onAutoFetch, onFetchAllDistricts,
}) {
    return (
        <div className="flex flex-wrap items-end gap-2 w-full lg:w-auto lg:ml-auto">
            {stateWide ? (
                <>
                    <button
                        type="button"
                        className="ag-btn-ghost"
                        disabled={!stateBatchDone || fetching || downloadingLive}
                        onClick={onDownloadLive}
                        title={stateBatchDone ? 'Re-download Excel without re-fetching' : 'Run fetch-all first'}
                    >
                        <Download className="h-4 w-4" />
                        {downloadingLive ? 'Building…' : 'Download Excel'}
                    </button>
                    <button
                        type="button"
                        className="ag-btn-ghost"
                        disabled={!canFetch || fetching}
                        onClick={() => onFetchAllDistricts({ resetFirst: true, downloadAfter: true })}
                    >
                        <RefreshCw className={`h-4 w-4 ${batchBusy ? 'animate-spin' : ''}`} />
                        {batchBusy ? `${batchProgress.current}/${batchProgress.total}` : 'Refresh all & download'}
                    </button>
                    <button
                        type="button"
                        className="ag-btn-primary"
                        disabled={!canFetch || fetching}
                        onClick={() => onFetchAllDistricts({ resetFirst: false, downloadAfter: true })}
                    >
                        <Zap className="h-4 w-4" />
                        {batchBusy ? 'Fetching districts…' : 'Fetch all & download Excel'}
                    </button>
                </>
            ) : (
                <>
                    <button
                        type="button"
                        className="ag-btn-ghost"
                        disabled={!fetched || fetching || downloadingLive}
                        onClick={onDownloadLive}
                        title={fetched ? 'Download Excel matching fetched results' : 'Fetch data first to enable download'}
                    >
                        <Download className="h-4 w-4" />
                        {downloadingLive ? 'Building…' : 'Download'}
                    </button>
                    <button
                        type="button"
                        className="ag-btn-ghost"
                        disabled={!canFetch || fetching}
                        onClick={() => onAutoFetch({ resetFirst: true })}
                    >
                        <RefreshCw className={`h-4 w-4 ${fetching ? 'animate-spin' : ''}`} />
                        Reset &amp; fetch
                    </button>
                    <button
                        type="button"
                        className="ag-btn-primary"
                        disabled={!canFetch || fetching}
                        onClick={() => onAutoFetch()}
                    >
                        <Zap className="h-4 w-4" />
                        {fetching ? 'Fetching…' : 'Fill empty only'}
                    </button>
                </>
            )}
        </div>
    );
}

function ScoreHeadline({ preview, stateWide, state, stateCentreRollup, inline = false }) {
    const s = preview.aoiScore || 0;
    const minTotal = stateCentreRollup?.totalCentresMin ?? stateCentreRollup?.totalCentres;
    const centreLabel = stateWide && minTotal != null
        ? `${state}: ~${minTotal} min centres`
        : (preview.centrePotential || '—');
    const subLabel = stateWide
        ? `State-wide AOI · ${stateCentreRollup?.districtCount ?? '—'} districts`
        : preview.territoryType;

    if (inline) {
        return (
            <div className="rounded-xl border border-brand-teal/30 bg-gradient-to-r from-brand-teal-50 to-white px-3 py-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                <div className="text-[10px] uppercase tracking-wide text-ink-500 font-bold shrink-0">Live AOI score</div>
                <div className="flex items-baseline gap-1.5">
                    <div className="text-xl font-extrabold tabular-nums text-brand-teal-800">{s.toFixed(1)}</div>
                    <div className="text-xs text-ink-500">/ 100</div>
                </div>
                <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${scoreTone(s)}`}>{centreLabel}</span>
                {subLabel && (
                    <span className="text-[10px] text-ink-500">{subLabel}</span>
                )}
            </div>
        );
    }

    return (
        <div className="rounded-xl border border-brand-teal/30 bg-gradient-to-br from-brand-teal-50 to-white p-2.5 min-w-[168px] shrink-0">
            <div className="text-[10px] uppercase tracking-wide text-ink-500 font-bold">Live AOI score</div>
            <div className="flex items-baseline gap-2 mt-0.5">
                <div className="text-2xl font-extrabold tabular-nums text-brand-teal-800">{s.toFixed(1)}</div>
                <div className="text-xs text-ink-500">/ 100</div>
            </div>
            <div className="mt-1 text-xs">
                <span className={`px-1.5 py-0.5 rounded font-bold ${scoreTone(s)}`}>{centreLabel}</span>
                {subLabel && (
                    <div className="text-[10px] text-ink-500 mt-0.5 leading-tight">{subLabel}</div>
                )}
            </div>
        </div>
    );
}

function AutoFetchSummary({ fetched, district, state }) {
    const osm = fetched.providers?.osm;
    const wiki = fetched.providers?.wiki;
    const google = fetched.providers?.google;
    const isState = fetched.scope === 'state' || district === ALL_DISTRICTS;
    const tierLabel = fetched.cityTier === 'state'
        ? 'State-wide aggregate'
        : fetched.cityTier === 'tier1'
            ? 'Tier-1 metro'
            : fetched.cityTier === 'tier2'
                ? 'Tier-2 city'
                : 'Tier-3 / emerging';
    const title = fetched.territoryLabel || districtDisplayName(district, state);

    return (
        <div className="ag-card p-4 space-y-4 border-emerald-200/60 bg-emerald-50/40">
            <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="font-semibold text-emerald-900">Sources for {title}</span>
                <span className="ag-chip bg-white text-xs">{tierLabel}</span>
                {isState && fetched.stateDistrictCount && (
                    <span className="ag-chip bg-white text-xs">{fetched.stateDistrictCount} districts</span>
                )}
                {fetched.tookMs != null && (
                    <span className="text-xs text-ink-500">{(fetched.tookMs / 1000).toFixed(1)}s</span>
                )}
            </div>

            {/* {fetched.rawCounts?.rows?.length > 0 && (
                <RawCountsPanel rawCounts={fetched.rawCounts} scope={fetched.scope} />
            )} */}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                <ProviderChip
                    ok={!!osm?.counts}
                    label="OpenStreetMap"
                    detail={osm?.counts
                        ? `${Object.values(osm.counts).reduce((s, n) => s + (Number(n) || 0), 0).toLocaleString('en-IN')} POIs total`
                        : osm?.error || 'No data'}
                />
                <ProviderChip
                    ok={!!wiki?.stats}
                    label="Wikidata"
                    detail={wiki?.stats?.population
                        ? `${(wiki.stats.population / 100000).toFixed(1)}L population${wiki.stats.area ? ` · ${Math.round(wiki.stats.area).toLocaleString('en-IN')} km²` : ''}`
                        : wiki?.error || 'No match'}
                />
                <ProviderChip
                    ok={!!google?.counts}
                    label="Google Places"
                    detail={google?.skipped
                        ? 'Skipped — set GOOGLE_MAPS_API_KEY in backend .env (Places API enabled)'
                        : google?.counts
                            ? `${Object.values(google.counts).reduce((s, n) => s + (Number(n) || 0), 0)} place results (capped per query)`
                            : google?.error || 'Unavailable'}
                />
            </div>
            <p className="text-[11px] text-ink-500 inline-flex items-start gap-1">
                <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                Chips marked <b>locked</b> already have a value — auto-fetch won&apos;t overwrite them. Use <b>Reset &amp; fetch</b> for a fresh pull.
            </p>
            {fetched.providers?.osm?.error && (
                <p className="text-[11px] text-rose-700 inline-flex items-start gap-1">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                    OpenStreetMap failed ({fetched.providers.osm.error}). Gym/hospital/mall scores may be low — Google Places is used as backup where available.
                </p>
            )}
        </div>
    );
}

function ProviderChip({ ok, label, detail }) {
    return (
        <div className={`rounded-lg border px-3 py-2 ${ok ? 'border-emerald-200 bg-white' : 'border-ink-100 bg-ink-50/50'}`}>
            <div className={`font-semibold ${ok ? 'text-emerald-800' : 'text-ink-600'}`}>{label}</div>
            <div className="text-ink-500 mt-0.5" title={detail}>{detail}</div>
        </div>
    );
}

function RawCountsPanel({ rawCounts, scope }) {
    const bySource = useMemo(() => {
        const map = {};
        for (const row of rawCounts.rows || []) {
            if (!map[row.source]) map[row.source] = [];
            map[row.source].push(row);
        }
        return map;
    }, [rawCounts.rows]);

    return (
        <div className="rounded-xl border border-emerald-200/80 bg-white overflow-hidden">
            <div className="px-4 py-2 border-b border-emerald-100 bg-emerald-50/50 flex items-center justify-between gap-2 flex-wrap">
                <span className="text-xs font-bold uppercase tracking-wide text-emerald-900">
                    Raw counts {scope === 'state' ? '(state-wide)' : scope === 'area' ? '(district boundary)' : '(district bbox)'}
                </span>
                <span className="text-[10px] text-ink-500">Actual numbers from data sources</span>
            </div>
            <div className="p-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
                {Object.entries(bySource).map(([source, rows]) => (
                    <div key={source}>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-ink-500 mb-2">{source}</div>
                        <ul className="space-y-1">
                            {rows.map(row => (
                                <li key={`${source}-${row.key}`} className="flex items-center justify-between gap-2 text-xs">
                                    <span className="text-ink-700 truncate" title={row.label}>{row.label}</span>
                                    <span className="font-mono font-bold tabular-nums text-emerald-800 shrink-0">
                                        {row.formatted ?? Number(row.count).toLocaleString('en-IN')}
                                        {source === 'Google Places' && row.key === 'gyms' && Number(row.count) === 20 && (
                                            <span className="text-[9px] font-normal text-ink-400 ml-0.5">min</span>
                                        )}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>
        </div>
    );
}

function CategoryCard({ cat, values, suggestions, confidence, categoryScore, onChange, onApplySuggestion }) {
    return (
        <div className={`ag-card overflow-hidden border ${cat.accent.replace(/text-\S+/, '').replace(/bg-\S+/, '')}`}>
            <div className={`px-4 py-2 border-b flex items-center justify-between ${cat.accent}`}>
                <div>
                    <div className="font-bold text-sm">{cat.title}</div>
                    <div className="text-[10px] opacity-80">
                        Parent weight {cat.parentWeight}% of AOI
                        {confidence && (
                            <span className="ml-2 opacity-70">· {confidence} confidence</span>
                        )}
                    </div>
                </div>
                <div className={`px-2 py-1 rounded text-xs font-bold ${scoreTone(categoryScore)}`}>
                    {categoryScore.toFixed(1)} / 100
                </div>
            </div>
            <div className="p-4 space-y-3">
                {cat.subs.map(s => {
                    const current = values[s.key] ?? 0;
                    const suggested = suggestions?.[s.key];
                    const showChip = Number.isFinite(suggested) && suggested > 0;
                    const canApply = showChip && current === 0;

                    return (
                        <div key={s.key} className="space-y-1">
                            <div className="grid grid-cols-[1fr_60px_auto] items-center gap-2">
                                <div>
                                    <div className="text-xs font-medium">{s.label}</div>
                                    <div className="text-[10px] text-ink-500">{s.weight}% internal weight</div>
                                </div>
                                <input
                                    type="number" min="0" max="100" step="1"
                                    className="ag-input text-xs py-1 text-right tabular-nums w-16"
                                    value={current}
                                    onChange={e => onChange(s.key, e.target.value)}
                                />
                                <input
                                    type="range" min="0" max="100" step="1"
                                    className="w-28"
                                    value={current}
                                    onChange={e => onChange(s.key, e.target.value)}
                                />
                            </div>
                            {showChip && (
                                <button
                                    type="button"
                                    disabled={!canApply}
                                    onClick={() => onApplySuggestion(s.key, suggested)}
                                    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold transition ${canApply
                                        ? 'bg-brand-teal-50 text-brand-teal-800 ring-1 ring-brand-teal/30 hover:bg-brand-teal-100 cursor-pointer'
                                        : 'bg-ink-50 text-ink-400 ring-1 ring-ink-100 cursor-default'
                                        }`}
                                    title={canApply ? 'Click to apply suggested value' : 'Suggestion (field already has a value)'}
                                >
                                    <Zap className="h-3 w-3" />
                                    Suggested {Math.round(suggested)}
                                    {!canApply && ' · locked'}
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

/* ─── Formula tab ────────────────────────────────────────────────────────── */
function FormulaTab({ meta }) {
    if (!meta) return <div className="ag-card p-8 text-sm text-ink-500">Loading…</div>;
    return (
        <div className="space-y-3">
            <div className="ag-card p-5">
                <h2 className="font-extrabold text-lg flex items-center gap-2">
                    <FileText className="h-5 w-5 text-brand-teal-700" /> AOI Formula
                </h2>
                <p className="text-sm text-ink-500 mt-1 max-w-3xl">
                    Each parameter category is a weighted average of its sub-components. The final AOI score is
                    the weighted sum of all 7 categories. Parent weights sum to 100%.
                </p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {CATEGORIES.map(c => (
                    <div key={c.key} className="ag-card overflow-hidden">
                        <div className={`px-4 py-2 border-b ${c.accent} flex items-center justify-between`}>
                            <div className="font-bold text-sm">{c.title}</div>
                            <span className="ag-chip bg-white/60 border border-current/20 text-xs font-bold">
                                {c.parentWeight}%
                            </span>
                        </div>
                        <div className="p-4 space-y-2">
                            <div className="text-xs text-ink-700 leading-relaxed">
                                {meta.formulas[c.key]}
                            </div>
                            <div className="border-t border-ink-100 pt-2 text-[11px] text-ink-500">
                                Sub-component weights:
                                <ul className="mt-1 grid grid-cols-2 gap-y-0.5">
                                    {c.subs.map(s => (
                                        <li key={s.key} className="inline-flex items-center gap-1">
                                            <ChevronRight className="h-3 w-3 text-ink-400" />
                                            <span>{s.label}</span>
                                            <span className="font-bold text-ink-700">{s.weight}%</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            <div className="ag-card p-5 bg-gradient-to-br from-brand-teal-50 to-white border-brand-teal/20">
                <div className="text-xs uppercase tracking-wide text-ink-500 font-bold">Final AOI</div>
                <div className="font-mono text-sm mt-2 leading-relaxed">
                    AOI Score = Σ (Category Score × Parent Weight)<br />
                    = AD × 25% + WM × 20% + UC × 15% + CE × 15% + HA × 10% + LD × 10% + CW × 5%
                </div>
            </div>
        </div>
    );
}

/* ─── Scoring Logic tab ──────────────────────────────────────────────────── */
function ScoringTab({ meta }) {
    if (!meta) return <div className="ag-card p-8 text-sm text-ink-500">Loading…</div>;
    return (
        <div className="space-y-3">
            <div className="ag-card p-5">
                <h2 className="font-extrabold text-lg flex items-center gap-2">
                    <BookOpen className="h-5 w-5 text-brand-teal-700" /> Scoring Logic
                </h2>
                <p className="text-sm text-ink-500 mt-1 max-w-3xl">
                    What each score band means per parameter, plus the AOI → centre-potential lookup.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {CATEGORIES.map(c => (
                    <div key={c.key} className="ag-card overflow-hidden">
                        <div className={`px-4 py-2 border-b ${c.accent}`}>
                            <div className="font-bold text-sm">{c.title}</div>
                        </div>
                        <table className="w-full text-xs">
                            <thead className="text-[10px] uppercase text-ink-500 bg-ink-50/60">
                                <tr><th className="text-left px-3 py-1.5">Score</th><th className="text-left px-3 py-1.5">Meaning</th></tr>
                            </thead>
                            <tbody className="divide-y divide-ink-100">
                                {(meta.categoryBands[c.key] || []).map((b, i, arr) => {
                                    const next = arr[i - 1]?.min;
                                    const range = next ? `${b.min}–${next}` : `${b.min}+`;
                                    return (
                                        <tr key={b.min}>
                                            <td className={`px-3 py-1.5 font-mono font-bold ${scoreTone(b.min)}`}>{range}</td>
                                            <td className="px-3 py-1.5">{b.label}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ))}
            </div>

            <div className="ag-card overflow-hidden border-brand-teal/30">
                <div className="px-4 py-2 border-b bg-brand-teal-50 text-brand-teal-900 flex items-center gap-2">
                    <ListChecks className="h-4 w-4" />
                    <div className="font-bold text-sm">AOI Score → Centre Potential</div>
                </div>
                <table className="w-full text-sm">
                    <thead className="text-[10px] uppercase text-ink-500 bg-ink-50/60">
                        <tr><th className="text-left px-4 py-2">Score range</th><th className="text-left px-4 py-2">Centre potential</th><th className="text-left px-4 py-2">Territory type</th></tr>
                    </thead>
                    <tbody className="divide-y divide-ink-100">
                        {(meta.aoiBands || []).map((b, i, arr) => {
                            const next = arr[i - 1]?.min;
                            const range = next ? `${b.min}–${next}` : `${b.min}+`;
                            return (
                                <tr key={b.min}>
                                    <td className={`px-4 py-2 font-mono font-bold ${scoreTone(b.min)}`}>{range}</td>
                                    <td className="px-4 py-2 font-semibold">{b.label}</td>
                                    <td className="px-4 py-2 text-ink-600">{b.territoryType}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

/* ─── Saved History tab ──────────────────────────────────────────────────── */
function HistoryTab({ items, filter, setFilter, onLoad, onDelete, stateOptions }) {
    /* J7 — Excel downloads. Two buttons:
         • State-wise — one row per state with aggregated centre totals.
         • District-wise — one row per district (all districts when state filter set),
           10-column layout: district, min total centres, 7 category scores, AOI.
       The filter.state is respected by the district-wise export so admin
       can download every district in one state. */
    const [downloading, setDownloading] = useState('');
    const downloadXlsx = async (kind) => {
        setDownloading(kind);
        try {
            const url = kind === 'states'
                ? '/aoi/export/states'
                : `/aoi/export/districts${filter.state ? `?state=${encodeURIComponent(filter.state)}` : ''}`;
            const res = await api.get(url, { responseType: 'blob', timeout: 60_000 });
            const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = kind === 'states'
                ? `aoi-states-${new Date().toISOString().slice(0, 10)}.xlsx`
                : `aoi-districts-${filter.state ? filter.state.replace(/\s+/g, '-') + '-' : ''}${new Date().toISOString().slice(0, 10)}.xlsx`;
            a.click();
            toast.success(`Downloaded ${kind === 'states' ? 'state-wise' : 'district-wise'} Excel`);
        } catch (e) {
            toast.error(e?.response?.data?.error || 'Download failed');
        } finally { setDownloading(''); }
    };

    return (
        <div className="space-y-3">
            <div className="ag-card p-4 flex flex-wrap items-end gap-3">
                <div className="min-w-[200px] flex-1">
                    <label className="ag-label">Filter by state</label>
                    <select className="ag-input" value={filter.state} onChange={e => setFilter(f => ({ ...f, state: e.target.value, district: '' }))}>
                        <option value="">All states</option>
                        {stateOptions.map(s => <option key={s.state} value={s.state}>{s.state}</option>)}
                    </select>
                </div>
                <div className="min-w-[200px] flex-1">
                    <label className="ag-label">Filter by district</label>
                    <input className="ag-input" placeholder="(any)" value={filter.district}
                        onChange={e => setFilter(f => ({ ...f, district: e.target.value }))} />
                </div>
                <div className="ml-auto flex items-center gap-2">
                    <span className="text-xs text-ink-500 mr-2">{items.length} record{items.length === 1 ? '' : 's'}</span>
                    <button className="ag-btn-ghost text-xs" disabled={downloading === 'districts'} onClick={() => downloadXlsx('districts')}>
                        <Download className="h-3.5 w-3.5" />
                        {downloading === 'districts' ? 'Building…' : (filter.state ? `Excel · all ${filter.state} districts` : 'District-wise Excel')}
                    </button>
                    <button className="ag-btn-primary text-xs" disabled={downloading === 'states'} onClick={() => downloadXlsx('states')}>
                        <Globe2 className="h-3.5 w-3.5" />
                        {downloading === 'states' ? 'Building…' : 'State-wise Excel'}
                    </button>
                </div>
            </div>

            <div className="ag-card p-0 overflow-x-auto">
                <table className="ag-table">
                    <thead><tr>
                        <th>State / District</th>
                        <th>AOI</th>
                        <th>Centre potential</th>
                        <th>Territory type</th>
                        <th>Scored by</th>
                        <th>When</th>
                        <th></th>
                    </tr></thead>
                    <tbody>
                        {items.map(it => (
                            <HistoryRow
                                key={it._id}
                                item={it}
                                onLoad={onLoad}
                                onDelete={onDelete}
                            />
                        ))}
                        {items.length === 0 && (
                            <tr><td colSpan={7} className="text-center text-ink-500 py-10 text-sm">
                                No saved scorecards yet. Go to the <b>Calculator</b> tab to score your first district.
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

/* J8 — One row of the history table, with per-row Download button that
   pulls the premium single-scorecard Excel for that record. Kept as a
   separate component so its `downloading` state stays local. */
function HistoryRow({ item, onLoad, onDelete }) {
    const [downloading, setDownloading] = useState(false);
    const download = async () => {
        setDownloading(true);
        try {
            const res = await api.get(`/aoi/scores/${item._id}/export`, { responseType: 'blob', timeout: 60_000 });
            const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            const safe = (s) => String(s || '').replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '');
            a.download = `aoi-${safe(item.district)}-${safe(item.state)}-${new Date().toISOString().slice(0, 10)}.xlsx`;
            a.click();
            toast.success(`Downloaded scorecard for ${item.district}`);
        } catch (e) {
            toast.error(e?.response?.data?.error || 'Download failed');
        } finally { setDownloading(false); }
    };
    return (
        <tr className="hover:bg-brand-teal-50/30">
            <td>
                <div className="font-medium">{item.district}</div>
                <div className="text-[11px] text-ink-500">{item.state}</div>
            </td>
            <td>
                <span className={`ag-chip ${scoreTone(item.aoiScore)} font-bold`}>{Number(item.aoiScore).toFixed(1)}</span>
            </td>
            <td className="text-sm font-semibold">{item.centrePotential}</td>
            <td className="text-xs text-ink-600">{item.territoryType}</td>
            <td className="text-xs">{item.createdBy?.name || '—'}</td>
            <td className="text-xs text-ink-500">{new Date(item.createdAt).toLocaleString('en-IN')}</td>
            <td className="text-right">
                <div className="flex gap-1 justify-end">
                    <button className="ag-btn-ghost text-xs" onClick={() => onLoad(item)}>Load</button>
                    <button
                        className="ag-btn-ghost text-xs"
                        disabled={downloading}
                        onClick={download}
                        title="Download district table Excel"
                    >
                        <Download className="h-3.5 w-3.5" />
                        {downloading ? '…' : 'Download'}
                    </button>
                    <button className="ag-btn-danger text-xs" onClick={() => onDelete(item._id, `${item.district}, ${item.state}`)}>
                        <Trash2 className="h-3.5 w-3.5" />
                    </button>
                </div>
            </td>
        </tr>
    );
}
